/**
 * The tenant's users (Phase 7b): email, status, groups, and the row actions a
 * status allows — resend an invite not yet accepted (or send a first one to a
 * user from before invites, who has no other way in), deactivate, reactivate,
 * edit groups, delete. The signed-in admin's own row offers neither
 * deactivate nor delete (the server refuses both). `users`, `loading` and
 * `error` are independent props.
 */
import type { GroupWire, UserSummaryWire } from '@norna/api-contract/tenancy.js';
import { MailIcon, PlusIcon, RefreshCwIcon } from 'lucide-react';
import type { UserAction } from '../../api/admin.js';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';
import { UNKNOWN_HINT } from '../../lib/openEnum.js';

export type UsersTableProps = {
  readonly users: readonly UserSummaryWire[];
  readonly groups: readonly GroupWire[];
  readonly selfId: string;
  readonly loading: boolean;
  readonly error: string | null;
  /** The row whose action is in flight, or null. */
  readonly busyUserId: string | null;
  readonly onInvite: () => void;
  readonly onRetry: () => void;
  readonly onAction: (action: UserAction, userId: string) => void;
  readonly onEditGroups: (userId: string) => void;
};

// `unknown`: a status from a newer cloud (D37) — neutral, and offered no action (none is known to apply).
const STATUS_BADGE = { invited: 'warning', active: 'success', disabled: 'offline', unknown: 'secondary' } as const;

type RowProps = {
  readonly user: UserSummaryWire;
  readonly groupName: ReadonlyMap<string, string>;
  readonly self: boolean;
  readonly busy: boolean;
  readonly onAction: (action: UserAction, userId: string) => void;
  readonly onEditGroups: (userId: string) => void;
};

function UserActions({ user, self, busy, onAction, onEditGroups }: Omit<RowProps, 'groupName'>) {
  return (
    <div className="flex flex-wrap justify-end gap-1">
      {user.status === 'invited' || (!self && user.status === 'active' && user.invitedAt === null) ? (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction('resend', user.userId)} data-testid="resend-invite">
          <MailIcon />
          {user.status === 'invited' ? 'Resend' : 'Send invite'}
        </Button>
      ) : null}
      <Button size="sm" variant="outline" disabled={busy} onClick={() => onEditGroups(user.userId)} data-testid="edit-groups">
        Groups
      </Button>
      {!self && user.status !== 'disabled' && user.status !== 'unknown' ? (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction('deactivate', user.userId)} data-testid="deactivate-user">
          Deactivate
        </Button>
      ) : null}
      {user.status === 'disabled' ? (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onAction('reactivate', user.userId)} data-testid="reactivate-user">
          Reactivate
        </Button>
      ) : null}
      {self ? null : (
        <Button size="sm" variant="ghost" className="text-destructive" disabled={busy} onClick={() => onAction('delete', user.userId)} data-testid="delete-user">
          Delete
        </Button>
      )}
    </div>
  );
}

function UserRow(props: RowProps) {
  const { user, groupName, self } = props;
  return (
    <TableRow data-testid="user-row" data-status={user.status}>
      <TableCell className="font-medium">
        {user.email}
        {self ? <span className="ml-2 text-xs text-muted-foreground">(you)</span> : null}
      </TableCell>
      <TableCell>
        <Badge variant={STATUS_BADGE[user.status]} title={user.status === 'unknown' ? UNKNOWN_HINT : undefined}>
          {user.status}
        </Badge>
        {user.status === 'invited' && user.invitedAt !== null ? <span className="ml-2 text-xs text-muted-foreground">sent {user.invitedAt.slice(0, 10)}</span> : null}
      </TableCell>
      <TableCell className="text-sm">{user.groupIds.length === 0 ? <span className="text-muted-foreground">none</span> : user.groupIds.map((id) => groupName.get(id) ?? id).join(', ')}</TableCell>
      <TableCell>
        <UserActions {...props} />
      </TableCell>
    </TableRow>
  );
}

export function UsersTable({ users, groups, selfId, loading, error, busyUserId, onInvite, onRetry, onAction, onEditGroups }: UsersTableProps) {
  const groupName = new Map(groups.map((g) => [g.groupId, g.name]));
  return (
    <Card data-testid="users-table">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Users</CardTitle>
          <CardDescription>An invite emails a one-time link to set a password. Deactivating ends a user’s access at once and keeps their groups.</CardDescription>
        </div>
        <Button onClick={onInvite} data-testid="invite-user">
          <PlusIcon />
          Invite user
        </Button>
      </CardHeader>
      <CardContent>
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load users: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {loading && users.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading users…</p> : null}
        {users.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Groups</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <UserRow key={u.userId} user={u} groupName={groupName} self={u.userId === selfId} busy={busyUserId === u.userId} onAction={onAction} onEditGroups={onEditGroups} />
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
